import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import {
  Target, AlertTriangle, Brain, BookOpen, ArrowRight,
  ClipboardList, RefreshCw, TrendingUp, Zap, Star, Flame
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import Navbar from "@/components/Navbar";

const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

export default function PersonalDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  useEffect(() => { fetchAnalytics(); }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-blue-500/30 border-t-orange-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const mp = data?.marks_prediction || {};
  const studyPlan = data?.study_plan || [];
  const stats = data?.stats || {};
  const weakTopics = data?.weak_topics || [];
  const smartRevision = data?.smart_revision || [];
  const aiUsage = data?.ai_usage || {};

  const totalDuration = studyPlan.reduce((sum, item) => {
    const mins = parseInt(item.duration) || 0;
    return sum + mins;
  }, 0);

  const handleStudyClick = (item) => {
    if (item.type === "practice") { navigate("/test"); return; }
    if (item.type === "test") { navigate("/test"); return; }
    if (item.chapter_id) { navigate(`/chapter/${item.chapter_id}`); return; }
    if (item.subject_id) { navigate(`/subject/${item.subject_id}`); return; }
    navigate("/home");
  };

  const scoreColor = mp.projected_score >= 70 ? "text-green-400" : mp.projected_score >= 50 ? "text-orange-400" : "text-red-400";
  const scoreRingColor = mp.projected_score >= 70 ? "#22c55e" : mp.projected_score >= 50 ? "#f97316" : "#ef4444";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="personal-dashboard">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div {...fadeUp} className="mb-6">
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-800">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Your personalized study analytics</p>
        </div>

        {/* Stats Row */}
        <div {...fadeUp} className="grid grid-cols-4 gap-3 mb-6">
          {[
            { icon: Star, label: "XP", value: stats.xp || 0, color: "text-orange-400", bg: "bg-orange-500/10" },
            { icon: Flame, label: "Streak", value: stats.streak || 0, color: "text-rose-400", bg: "bg-rose-500/10" },
            { icon: Brain, label: "Tests", value: stats.total_tests || 0, color: "text-blue-400", bg: "bg-blue-500/10" },
            { icon: BookOpen, label: "Studied", value: stats.total_activities || 0, color: "text-emerald-400", bg: "bg-emerald-500/10" },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-2xl p-3 sm:p-4 border border-slate-200 text-center" data-testid={`dash-stat-${s.label.toLowerCase()}`}>
              <s.icon className={`w-5 h-5 ${s.color} mx-auto mb-1`} strokeWidth={2.5} />
              <p className="font-heading text-lg sm:text-xl font-extrabold text-slate-800">{s.value}</p>
              <p className="text-[10px] text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Today's Study Plan */}
        <div {...fadeUp} className="bg-white rounded-2xl p-5 border border-slate-200 mb-5" data-testid="study-plan-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-base font-bold text-slate-800">Today's Study Plan</h2>
            <span className="text-xs bg-green-500/20 text-green-400 px-3 py-1 rounded-full font-semibold">{totalDuration} min</span>
          </div>
          <div className="space-y-2.5 mb-4">
            {studyPlan.map((item, i) => {
              const colors = {
                learn: { dot: "bg-green-400", bg: "bg-green-500/10 border-green-500/20", icon: "text-green-400" },
                practice: { dot: "bg-blue-400", bg: "bg-blue-500/10 border-blue-500/20", icon: "text-blue-400" },
                test: { dot: "bg-orange-400", bg: "bg-orange-500/10 border-orange-500/20", icon: "text-orange-400" },
              }[item.type] || { dot: "bg-white/40", bg: "bg-white border-slate-200", icon: "text-slate-400" };
              return (
                <button key={i} onClick={() => handleStudyClick(item)} data-testid={`study-item-${i}`}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border ${colors.bg} hover:bg-white transition-all text-left group`}>
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${colors.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-400 uppercase">{item.type === "learn" ? item.subject : item.type}</span>
                      <span className="text-[10px] text-slate-300">·</span>
                      <span className="text-[10px] text-slate-300">{item.duration}</span>
                    </div>
                    <p className="text-sm text-slate-800 font-medium truncate">{item.chapter}{item.type === "learn" ? ` — ${item.topic}` : ""}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Marks Prediction + Weak Topics Row */}
        <div className="grid sm:grid-cols-2 gap-5 mb-5">
          {/* Marks Prediction */}
          <div {...fadeUp} className="bg-white rounded-2xl p-5 shadow-soft" data-testid="marks-prediction-card">
            <h3 className="font-heading text-base font-bold text-slate-800 mb-3">Marks Prediction</h3>
            {mp.total_attempts > 0 ? (
              <>
                <div className="flex items-center justify-center mb-3">
                  <div className="relative w-28 h-28">
                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                      <circle cx="50" cy="50" r="42" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                      <circle cx="50" cy="50" r="42" fill="none" stroke={scoreRingColor} strokeWidth="8"
                        strokeDasharray={`${mp.projected_score * 2.64} 264`} strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <p className="text-xs text-slate-400">Projected</p>
                      <p className={`font-heading text-2xl font-extrabold ${scoreColor}`}>{mp.projected_score}%</p>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-500 text-center">{mp.improvement_tip}</p>
              </>
            ) : (
              <div className="text-center py-4">
                <TrendingUp className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">Take tests to see your predicted score</p>
              </div>
            )}
          </div>

          {/* Weak Topics */}
          <div {...fadeUp} className="bg-white rounded-2xl p-5 shadow-soft" data-testid="weak-topics-card">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              <h3 className="font-heading text-base font-bold text-slate-800">Weak Topics</h3>
            </div>
            {weakTopics.length > 0 ? (
              <div className="space-y-2.5">
                {weakTopics.slice(0, 4).map((wt, i) => {
                  const severity = wt.wrong_count >= 3 ? "bg-red-100 text-red-600" : wt.wrong_count >= 2 ? "bg-orange-100 text-orange-600" : "bg-yellow-100 text-yellow-700";
                  return (
                    <div key={i} className="flex items-center justify-between" data-testid={`weak-topic-${i}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${wt.wrong_count >= 3 ? 'bg-red-500' : wt.wrong_count >= 2 ? 'bg-orange-500' : 'bg-yellow-500'}`} />
                        <p className="text-sm text-slate-700 font-medium truncate">{wt.topic}</p>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${severity}`}>
                        {wt.wrong_count}x wrong
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4">
                <Target className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No weak topics yet. Keep practicing!</p>
              </div>
            )}
          </div>
        </div>

        {/* Smart Revision */}
        <div {...fadeUp} className="bg-white rounded-2xl p-5 border border-slate-200 mb-5" data-testid="smart-revision-card">
          <div className="flex items-center gap-2 mb-3">
            <RefreshCw className="w-4 h-4 text-blue-400" />
            <h3 className="font-heading text-base font-bold text-slate-800">Smart Revision</h3>
          </div>
          {smartRevision.length > 0 ? (
            <div className="space-y-2.5">
              {smartRevision.map((sr, i) => (
                <button key={i} data-testid={`revision-${i}`}
                  onClick={() => navigate(`/test`)}
                  className="w-full flex items-center justify-between p-3 rounded-xl bg-white border border-slate-200 hover:bg-white transition-all text-left">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sr.severity === 'high' ? 'bg-red-500' : 'bg-orange-400'}`} />
                    <div className="min-w-0">
                      <p className="text-sm text-slate-800 font-medium truncate">{sr.topic}</p>
                      <p className="text-xs text-slate-400">{sr.reason}</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : (
                <p className="text-sm text-slate-400 py-2">Complete tests to get personalized revision suggestions</p>
          )}
        </div>

        {/* Quick Test CTA */}
        <div {...fadeUp}>
          <button data-testid="quick-test-btn"
            onClick={() => navigate("/test")}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-5 flex items-center justify-between hover:shadow-xl transition-all group">
            <div className="flex items-center gap-3">
              <ClipboardList className="w-6 h-6 text-slate-800" />
              <div className="text-left">
                <h3 className="font-heading font-bold text-slate-800">Take a Quick Test</h3>
                <p className="text-slate-500 text-xs">Test your skills with MCQ + Written</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-500 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* AI Usage for free users */}
        {!user?.is_premium && (
          <div {...fadeUp} className="mt-5 bg-blue-500/10 rounded-2xl p-4 border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-blue-400" />
                <p className="text-sm text-blue-300">AI Questions Today: <span className="font-bold text-slate-800">{aiUsage.remaining || 0}/{aiUsage.limit || 10}</span> remaining</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
